import { ClassroomDisplay } from './pages/v2/teacher/ClassroomDisplay';
import { TeachingStudio } from './pages/v2/teacher/TeachingStudio';
import { StudentMonitor } from './pages/v2/teacher/StudentMonitor';
import { TeacherWorkspace } from './components/layout/TeacherWorkspace';
import { ClassroomSession } from './components/v2/student/ClassroomSession';
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { StudentPreview, StudentPreviewPage } from './components/auth/StudentPreview';
import { previewLocation } from './services/studentPreview';
import { RouteAwareHeader } from './components/layout/Header';
import { Login } from './pages/auth/Login';
import { TeacherDashboard } from './pages/teacher/TeacherDashboard';
import { TasksEvidenceTest } from './pages/teacher/TasksEvidenceTest';
import { StudentActivity } from './pages/student/StudentActivity';
import { V2SectionDashboard } from './pages/v2/teacher/SectionDashboard';
import ClassOverview from './pages/v2/teacher/ClassOverview';
import ReportingCycles from './pages/v2/teacher/ReportingCycles';
import { V2LessonPlanEditor } from './pages/v2/teacher/LessonPlanEditor';
import { V2LessonControl } from './pages/v2/teacher/LessonControl';
import { V2LessonOps } from './pages/v2/teacher/LessonOps';
import { V2LessonDisplay } from './pages/v2/teacher/LessonDisplay';
import { V2RunResolver } from './pages/v2/teacher/RunResolver';
import { V2EvidenceReview } from './pages/v2/teacher/EvidenceReview';
import { V2MasteryOverview } from './pages/v2/teacher/MasteryOverview';
import { V2Plan } from './pages/v2/teacher/Plan';
import { V2PlanHookDetail } from './pages/v2/teacher/PlanHookDetail';
import { V2Students } from './pages/v2/teacher/Students';
import { V2Rollover } from './pages/v2/teacher/Rollover';
import { V2TeacherGoals } from './pages/v2/teacher/Goals';
import { V2CoachingPerformance } from './pages/v2/teacher/CoachingPerformance';
import { V2StudentCoachingRecord } from './pages/v2/teacher/StudentCoachingRecord';
import { V2CoachingConference } from './pages/v2/teacher/CoachingConference';
import { V2PerformanceReview } from './pages/v2/teacher/PerformanceReview';
import { V2TeacherReports } from './pages/v2/teacher/Reports';
import { V2SectionManage } from './pages/v2/teacher/SectionManage';
import { V2SectionArchiveManager } from './pages/v2/teacher/SectionArchiveManager';
import { V2TeacherQueue } from './pages/v2/teacher/Queue';
import { V2TeacherAssignmentReview } from './pages/v2/teacher/AssignmentReview';
import { V2AssessmentClassSummary } from './pages/v2/teacher/AssessmentClassSummary';
import { V2AssessmentBuilder } from './pages/v2/teacher/AssessmentBuilder';
import { V2Diagnostics } from './pages/v2/teacher/Diagnostics';
import { V2StudentWorkHistory } from './pages/v2/teacher/StudentWorkHistory';
import V2DailyCompletion from './pages/v2/teacher/DailyCompletion';
import { V2LessonPreparation } from './pages/v2/teacher/LessonPreparation';
import { UnitDayReview } from './pages/v2/teacher/UnitDayReview';
import { ClassroomPathway } from './pages/v2/teacher/ClassroomPathway';
import { PlanningWorkspace } from './pages/v2/teacher/PlanningWorkspace';
import { V2StudentDashboard } from './pages/v2/student/Dashboard';
import { ClassroomToday } from './components/v2/student/ClassroomToday';
import { UnitWorkspace } from './pages/v2/student/UnitWorkspace';
import { V2StudentCoaching } from './pages/v2/student/Coaching';
import { V2StudentWorkReview } from './pages/v2/student/WorkReview';
import { V2PerformanceSelfReview } from './pages/v2/student/PerformanceSelfReview';
import { V2StudentLessonRun } from './pages/v2/student/LessonRun';
import { AssignmentDetail } from './pages/v2/student/AssignmentDetail';
import { V2RunPresentation } from './pages/v2/student/RunPresentation';
import { V2RunReview } from './pages/v2/student/RunReview';
import { V2EvidenceLedger } from './pages/v2/student/EvidenceLedger';
import { V2StudentStandards } from './pages/v2/student/Standards';
import { V2DashboardRedirect } from './pages/v2/DashboardRedirect';
import { ToastProvider } from './components/ui/toast';
import './index.css';

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return <>{children}</>;
};

const RoleBasedRoute: React.FC<{ children: React.ReactNode; requiredRole: 'teacher' | 'student' }> = ({ 
  children, 
  requiredRole 
}) => {
  const { user } = useAuth();
  
  if (!user || user.role !== requiredRole) {
    return <Navigate to={user?.role === 'teacher' ? '/teacher' : '/student'} replace />;
  }
  
  return <>{children}</>;
};

const WithDnd: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <DndProvider backend={HTML5Backend}>{children}</DndProvider>
);

const AppContent: React.FC = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <ToastProvider>
      <Router basename={previewLocation()?.basename}>
        <StudentPreviewPage>
        <div className="min-h-screen bg-gray-50">
          {user && <RouteAwareHeader />}
          
          <main>
            <TeacherWorkspace enabled={user?.role === 'teacher'}><Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/" element={<Navigate to={user ? '/dashboard' : '/login'} replace />} />
            
            <Route path="/teacher" element={
              <ProtectedRoute>
                <RoleBasedRoute requiredRole="teacher">
                  <TeacherDashboard />
                </RoleBasedRoute>
              </ProtectedRoute>
            } />

            <Route path="/teacher/tasks-test" element={
              <ProtectedRoute>
                <RoleBasedRoute requiredRole="teacher">
                  <TasksEvidenceTest />
                </RoleBasedRoute>
              </ProtectedRoute>
            } />

            <Route path="/v2/teacher" element={
              <ProtectedRoute>
                <RoleBasedRoute requiredRole="teacher">
                  <ClassOverview />
                </RoleBasedRoute>
              </ProtectedRoute>
            } />

            <Route path="/v2/teacher/teach" element={
              <ProtectedRoute>
                <RoleBasedRoute requiredRole="teacher">
                  <V2SectionDashboard />
                </RoleBasedRoute>
              </ProtectedRoute>
            } />

            <Route path="/v2/teacher/reporting-cycles" element={
              <ProtectedRoute>
                <RoleBasedRoute requiredRole="teacher"><ReportingCycles /></RoleBasedRoute>
              </ProtectedRoute>
            } />

            <Route path="/v2/teacher/pathways" element={<ProtectedRoute><RoleBasedRoute requiredRole="teacher"><ClassroomPathway /></RoleBasedRoute></ProtectedRoute>} />
            <Route path="/v2/teacher/planning" element={<ProtectedRoute><RoleBasedRoute requiredRole="teacher"><PlanningWorkspace /></RoleBasedRoute></ProtectedRoute>} />
            <Route path="/v2/teacher/unit-day-review" element={<ProtectedRoute><RoleBasedRoute requiredRole="teacher"><UnitDayReview /></RoleBasedRoute></ProtectedRoute>} />
            <Route path="/v2/teacher/prepare" element={
              <ProtectedRoute>
                <RoleBasedRoute requiredRole="teacher">
                  <V2LessonPreparation />
                </RoleBasedRoute>
              </ProtectedRoute>
            } />

            <Route path="/v2/teacher/lesson-plan" element={
              <ProtectedRoute>
                <RoleBasedRoute requiredRole="teacher">
                  <V2LessonPlanEditor />
                </RoleBasedRoute>
              </ProtectedRoute>
            } />

            <Route path="/v2/teacher/assessments" element={
              <ProtectedRoute>
                <RoleBasedRoute requiredRole="teacher">
                  <V2AssessmentBuilder />
                </RoleBasedRoute>
              </ProtectedRoute>
            } />

            <Route path="/v2/teacher/diagnostics" element={
              <ProtectedRoute><RoleBasedRoute requiredRole="teacher"><V2Diagnostics /></RoleBasedRoute></ProtectedRoute>
            } />

            <Route path="/v2/teacher/lesson-plan/:planId/edit" element={
              <ProtectedRoute>
                <RoleBasedRoute requiredRole="teacher">
                  <V2LessonPlanEditor />
                </RoleBasedRoute>
              </ProtectedRoute>
            } />

            <Route path="/v2/teacher/lesson-plan/:planId/student-preview" element={
              <ProtectedRoute>
                <RoleBasedRoute requiredRole="teacher">
                  <WithDnd>
                    <V2RunPresentation lessonPlanPreview />
                  </WithDnd>
                </RoleBasedRoute>
              </ProtectedRoute>
            } />

            <Route path="/v2/teacher/run/:runId" element={
              <ProtectedRoute>
                <RoleBasedRoute requiredRole="teacher">
                  <V2LessonControl />
                </RoleBasedRoute>
              </ProtectedRoute>
            } />

            <Route path="/v2/teacher/run/:runId/ops" element={
              <ProtectedRoute>
                <RoleBasedRoute requiredRole="teacher">
                  <V2LessonOps />
                </RoleBasedRoute>
              </ProtectedRoute>
            } />

            <Route path="/v2/teacher/classroom/:sectionId/studio" element={<ProtectedRoute><RoleBasedRoute requiredRole="teacher"><TeachingStudio /></RoleBasedRoute></ProtectedRoute>} />
            <Route path="/v2/teacher/classroom/:sectionId/display" element={<ProtectedRoute><RoleBasedRoute requiredRole="teacher"><ClassroomDisplay /></RoleBasedRoute></ProtectedRoute>} />
            <Route path="/v2/teacher/classroom/:sectionId/monitor" element={<ProtectedRoute><RoleBasedRoute requiredRole="teacher"><StudentMonitor /></RoleBasedRoute></ProtectedRoute>} />

            <Route path="/v2/teacher/run/:runId/display" element={
              <ProtectedRoute>
                <RoleBasedRoute requiredRole="teacher">
                  <V2LessonDisplay />
                </RoleBasedRoute>
              </ProtectedRoute>
            } />

            <Route path="/v2/teacher/run/:runId/presentation" element={
              <ProtectedRoute>
                <RoleBasedRoute requiredRole="teacher">
                  <WithDnd>
                    <V2RunPresentation allowControlSummary />
                  </WithDnd>
                </RoleBasedRoute>
              </ProtectedRoute>
            } />

            <Route path="/v2/teacher/run/:runId/student-preview" element={
              <ProtectedRoute>
                <RoleBasedRoute requiredRole="teacher">
                  <WithDnd>
                    <V2RunPresentation studentPreview />
                  </WithDnd>
                </RoleBasedRoute>
              </ProtectedRoute>
            } />

            <Route path="/v2/teacher/section/:sectionId/plan/:planId/run" element={
              <ProtectedRoute>
                <RoleBasedRoute requiredRole="teacher">
                  <V2RunResolver />
                </RoleBasedRoute>
              </ProtectedRoute>
            } />

            <Route path="/v2/teacher/review" element={
              <ProtectedRoute>
                <RoleBasedRoute requiredRole="teacher">
                  <V2EvidenceReview />
                </RoleBasedRoute>
              </ProtectedRoute>
            } />

            <Route path="/v2/teacher/mastery/:hookId" element={
              <ProtectedRoute>
                <RoleBasedRoute requiredRole="teacher">
                  <V2MasteryOverview />
                </RoleBasedRoute>
              </ProtectedRoute>
            } />

            <Route path="/v2/teacher/plan" element={
              <ProtectedRoute>
                <RoleBasedRoute requiredRole="teacher">
                  <V2Plan />
                </RoleBasedRoute>
              </ProtectedRoute>
            } />

            <Route path="/v2/teacher/plan/hooks/:hookId" element={
              <ProtectedRoute>
                <RoleBasedRoute requiredRole="teacher">
                  <V2PlanHookDetail />
                </RoleBasedRoute>
              </ProtectedRoute>
            } />

            <Route path="/v2/teacher/students" element={
              <ProtectedRoute>
                <RoleBasedRoute requiredRole="teacher">
                  <V2Students />
                </RoleBasedRoute>
              </ProtectedRoute>
            } />

            <Route path="/v2/teacher/rollover" element={
              <ProtectedRoute>
                <RoleBasedRoute requiredRole="teacher">
                  <V2Rollover />
                </RoleBasedRoute>
              </ProtectedRoute>
            } />

            <Route path="/v2/teacher/students/:studentId/work" element={
              <ProtectedRoute>
                <RoleBasedRoute requiredRole="teacher">
                  <V2StudentWorkHistory />
                </RoleBasedRoute>
              </ProtectedRoute>
            } />

            <Route path="/v2/teacher/goals" element={
              <ProtectedRoute>
                <RoleBasedRoute requiredRole="teacher">
                  <V2TeacherGoals />
                </RoleBasedRoute>
              </ProtectedRoute>
            } />

            <Route path="/v2/teacher/coaching" element={
              <ProtectedRoute>
                <RoleBasedRoute requiredRole="teacher">
                  <V2CoachingPerformance />
                </RoleBasedRoute>
              </ProtectedRoute>
            } />

            <Route path="/v2/teacher/coaching/sections/:sectionId/students/:studentId" element={
              <ProtectedRoute>
                <RoleBasedRoute requiredRole="teacher">
                  <V2StudentCoachingRecord />
                </RoleBasedRoute>
              </ProtectedRoute>
            } />

            <Route path="/v2/teacher/coaching/sections/:sectionId/students/:studentId/check-ins/:checkInId" element={
              <ProtectedRoute>
                <RoleBasedRoute requiredRole="teacher">
                  <V2CoachingConference />
                </RoleBasedRoute>
              </ProtectedRoute>
            } />

            <Route path="/v2/teacher/coaching/sections/:sectionId/students/:studentId/reviews/:cycleId" element={
              <ProtectedRoute>
                <RoleBasedRoute requiredRole="teacher">
                  <V2PerformanceReview />
                </RoleBasedRoute>
              </ProtectedRoute>
            } />

            <Route path="/v2/teacher/reports" element={
              <ProtectedRoute>
                <RoleBasedRoute requiredRole="teacher">
                  <V2TeacherReports />
                </RoleBasedRoute>
              </ProtectedRoute>
            } />

            <Route path="/v2/teacher/queue" element={
              <ProtectedRoute>
                <RoleBasedRoute requiredRole="teacher">
                  <V2TeacherQueue />
                </RoleBasedRoute>
              </ProtectedRoute>
            } />

            <Route path="/v2/teacher/daily-completion" element={
              <ProtectedRoute>
                <RoleBasedRoute requiredRole="teacher">
                  <V2DailyCompletion />
                </RoleBasedRoute>
              </ProtectedRoute>
            } />

            <Route path="/v2/teacher/assignments/:assignmentId/students/:studentId" element={
              <ProtectedRoute>
                <RoleBasedRoute requiredRole="teacher">
                  <V2TeacherAssignmentReview />
                </RoleBasedRoute>
              </ProtectedRoute>
            } />

            <Route path="/v2/teacher/assignments/:assignmentId/summary" element={
              <ProtectedRoute>
                <RoleBasedRoute requiredRole="teacher">
                  <V2AssessmentClassSummary />
                </RoleBasedRoute>
              </ProtectedRoute>
            } />

            <Route path="/v2/teacher/section/:sectionId" element={
              <ProtectedRoute>
                <RoleBasedRoute requiredRole="teacher">
                  <V2SectionManage />
                </RoleBasedRoute>
              </ProtectedRoute>
            } />

            <Route path="/v2/teacher/section-archives" element={
              <ProtectedRoute>
                <RoleBasedRoute requiredRole="teacher">
                  <V2SectionArchiveManager />
                </RoleBasedRoute>
              </ProtectedRoute>
            } />
            
            <Route path="/student" element={
              <ProtectedRoute>
                <RoleBasedRoute requiredRole="student">
                  <StudentActivity />
                </RoleBasedRoute>
              </ProtectedRoute>
            } />

            <Route path="/v2/student/units" element={
              <ProtectedRoute><RoleBasedRoute requiredRole="student"><UnitWorkspace /></RoleBasedRoute></ProtectedRoute>
            } />
            <Route path="/v2/student" element={
              <ProtectedRoute>
                <RoleBasedRoute requiredRole="student">
                  <ClassroomToday fallback={<V2StudentDashboard />} />
                </RoleBasedRoute>
              </ProtectedRoute>
            } />

            <Route path="/v2/student/coaching" element={
              <ProtectedRoute>
                <RoleBasedRoute requiredRole="student">
                  <V2StudentCoaching />
                </RoleBasedRoute>
              </ProtectedRoute>
            } />

            <Route path="/v2/student/standards" element={
              <ProtectedRoute><RoleBasedRoute requiredRole="student"><V2StudentStandards /></RoleBasedRoute></ProtectedRoute>
            } />

            <Route path="/v2/student/reviews/:goalId" element={
              <ProtectedRoute>
                <RoleBasedRoute requiredRole="student">
                  <V2PerformanceSelfReview />
                </RoleBasedRoute>
              </ProtectedRoute>
            } />

            <Route path="/v2/student/run/:runId" element={
              <ProtectedRoute>
                <RoleBasedRoute requiredRole="student">
                  <V2StudentLessonRun />
                </RoleBasedRoute>
              </ProtectedRoute>
            } />

            <Route path="/v2/student/run/:runId/review" element={
              <ProtectedRoute>
                <RoleBasedRoute requiredRole="student">
                  <V2RunReview />
                </RoleBasedRoute>
              </ProtectedRoute>
            } />

            <Route path="/v2/assignments/:assignmentId" element={
              <ProtectedRoute>
                <RoleBasedRoute requiredRole="student">
                  <WithDnd>
                    <AssignmentDetail />
                  </WithDnd>
                </RoleBasedRoute>
              </ProtectedRoute>
            } />

            <Route path="/v2/student/classroom/:sectionId" element={<ProtectedRoute><RoleBasedRoute requiredRole="student"><WithDnd><ClassroomSession /></WithDnd></RoleBasedRoute></ProtectedRoute>} />
            <Route path="/v2/run/:runId" element={
              <ProtectedRoute>
                <RoleBasedRoute requiredRole="student">
                  <WithDnd>
                    <V2RunPresentation />
                  </WithDnd>
                </RoleBasedRoute>
              </ProtectedRoute>
            } />

            <Route path="/v2/student/work-review" element={
              <ProtectedRoute>
                <RoleBasedRoute requiredRole="student">
                  <V2StudentWorkReview />
                </RoleBasedRoute>
              </ProtectedRoute>
            } />

            <Route path="/v2/student/evidence/:hookId" element={
              <ProtectedRoute>
                <RoleBasedRoute requiredRole="student">
                  <V2EvidenceLedger />
                </RoleBasedRoute>
              </ProtectedRoute>
            } />
            
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <V2DashboardRedirect />
              </ProtectedRoute>
            } />
            
            <Route path="*" element={<Navigate to="/" replace />} />
            </Routes></TeacherWorkspace>
          </main>
        </div>
        </StudentPreviewPage>
      </Router>
    </ToastProvider>
  );
};

function App() {
  return (
    <AuthProvider>
      <StudentPreview><AppContent /></StudentPreview>
    </AuthProvider>
  );
}

export default App;
